import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import NewsletterSignup from '../components/NewsletterSignup';

interface ProfileSummary {
  id: string;
  universityAttending: string | null;
  graduationYear: number | null;
  satScore: number | null;
  actScore: number | null;
  gpaWeighted: number | null;
  gpaUnweighted: number | null;
  gpaScale: number | null;
  major: string | null;
  totalApplications: number;
  totalAcceptances: number;
  totalRejections: number;
  totalWaitlisted: number;
  _count: {
    activities: number;
    awards: number;
    essays: number;
    apibScores: number;
    admissionResults: number;
  };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const admitLabel = (university: string | null, graduationYear: number | null) => {
  const school = university || 'Unknown university';
  return graduationYear ? `Admit · ${school} '${String(graduationYear).slice(-2)}` : `Admit · ${school}`;
};

const AdmitProfilesPage: React.FC = () => {
  const { user, isLoading } = useUser();
  const [universities, setUniversities] = useState<string[]>([]);
  const [graduationYears, setGraduationYears] = useState<number[]>([]);
  const [university, setUniversity] = useState('');
  const [graduationYear, setGraduationYear] = useState('');
  const [page, setPage] = useState(1);
  const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter options (once, when logged in)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    apiService.get('/admit-profiles/filters').then((data) => {
      if (cancelled || !data?.success) return;
      setUniversities(data.universities || []);
      setGraduationYears(data.graduationYears || []);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Profile list
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: '12' });
    if (university) params.set('university', university);
    if (graduationYear) params.set('graduationYear', graduationYear);
    apiService.get(`/admit-profiles?${params.toString()}`).then((data) => {
      if (cancelled) return;
      if (data?.success) {
        setProfiles(data.profiles || []);
        setPagination(data.pagination || null);
      } else {
        setError('Could not load admit profiles. Please try again in a moment.');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user, page, university, graduationYear]);

  const totalPages = pagination?.totalPages || 1;

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Admit profiles · anonymized
          </span>
          <h1 style={{ maxWidth: '26ch' }}>Real admitted-student profiles, in full.</h1>
          <p style={{ color: 'var(--ink-secondary)', marginTop: 16, maxWidth: '62ch' }}>
            Browse anonymized profiles of real admitted students — test scores, activities,
            awards, essays, and every admission decision. Filter by university and graduation
            year to see what worked for students headed where your family is aiming.
          </p>
        </div>
      </header>

      {/* Content */}
      <section className="section warm" style={{ paddingTop: 48 }}>
        <div className="wrap">
          {isLoading && (
            <p className="meta" style={{ textAlign: 'center', padding: '40px 0' }}>
              Loading…
            </p>
          )}

          {!isLoading && !user && (
            <div className="card" style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
              <h3 style={{ marginBottom: 10 }}>Sign in to browse admit profiles.</h3>
              <p className="meta" style={{ marginBottom: 18 }}>
                Full anonymized profiles — scores, activities, essays, and decisions — are free
                with any Common Room account.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link className="btn btn-primary" to="/login">
                  Sign in
                </Link>
                <Link className="btn btn-secondary" to="/family-signup">
                  Create a family account
                </Link>
              </div>
            </div>
          )}

          {!isLoading && user && (
            <>
              {/* Filters */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
                <select
                  value={university}
                  onChange={(e) => {
                    setUniversity(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Filter by university"
                >
                  <option value="">All universities</option>
                  {universities.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
                <select
                  value={graduationYear}
                  onChange={(e) => {
                    setGraduationYear(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Filter by graduation year"
                >
                  <option value="">All years</option>
                  {graduationYears.map((y) => (
                    <option key={y} value={String(y)}>
                      Class of {y}
                    </option>
                  ))}
                </select>
              </div>

              {loading && (
                <p className="meta" style={{ textAlign: 'center', padding: '40px 0' }}>
                  Loading admit profiles…
                </p>
              )}

              {!loading && error && (
                <div className="note" style={{ textAlign: 'center' }}>{error}</div>
              )}

              {!loading && !error && profiles.length === 0 && (
                <div className="note" style={{ textAlign: 'center' }}>
                  No profiles match these filters — try clearing the university or year.
                </div>
              )}

              {!loading && !error && profiles.length > 0 && (
                <div className="home-resources-grid">
                  {profiles.map((p) => (
                    <Link
                      key={p.id}
                      to={`/resources/admit-profiles/${p.id}`}
                      className="card"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <div className="meta" style={{ marginBottom: 8 }}>
                        {admitLabel(p.universityAttending, p.graduationYear)}
                      </div>
                      <h3 style={{ marginBottom: 10 }}>{p.major || 'Undeclared'}</h3>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        {p.satScore != null && <span className="chip">SAT {p.satScore}</span>}
                        {p.actScore != null && <span className="chip">ACT {p.actScore}</span>}
                        {p.gpaUnweighted != null && (
                          <span className="chip">GPA {p.gpaUnweighted}</span>
                        )}
                        {p.gpaUnweighted == null && p.gpaWeighted != null && (
                          <span className="chip">GPA {p.gpaWeighted} (w)</span>
                        )}
                      </div>
                      <p className="meta" style={{ fontSize: 14.5 }}>
                        {p.totalAcceptances} acceptance{p.totalAcceptances === 1 ? '' : 's'} ·{' '}
                        {p._count.essays} essay{p._count.essays === 1 ? '' : 's'} ·{' '}
                        {p._count.activities} activit{p._count.activities === 1 ? 'y' : 'ies'}
                      </p>
                    </Link>
                  ))}
                </div>
              )}

              {!loading && !error && pagination && pagination.totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 36
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  >
                    ← Prev
                  </button>
                  <span className="meta">
                    Page {pagination.page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <NewsletterSignup
        variant="band"
        source="admit-profiles"
        heading="New admit profiles, straight to your inbox."
        subtext="Subscribe and we’ll send new anonymized profiles and what we learn from them — free, no spam, unsubscribe anytime."
      />
    </>
  );
};

export default AdmitProfilesPage;
